import { useCallback, useState, useEffect } from 'react';
import { object, string } from 'yup';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { FormModal } from './FormModal';
import { TextInput, SelectInput, CheckboxInput } from '../inputs';
import {
  useDispatch,
  addResource,
  deleteResource,
  fetchResourceById,
} from '../../../state';
import { useRefresh, useNotify } from '../../../hooks';
import { convertNullFieldsToEmptyString } from '../../../lib';
import { FeeType } from '../../../types';

interface Props {
  open: boolean;
  handleClose: () => void;
  orderId?: string | number;
  orderFeeId?: string | number;
  customerId?: string | number | null;
  orderIds?: string[] | number[];
  isAppliedFee?: boolean;
  shouldRefresh?: boolean;
}

interface State {
  type: FeeType | '';
  name: string;
  amount: number | '';
  baseCost: number | '';
  customerFeeID?: string;
  quantity: number | '';
  billableToCustomer: boolean;
  note: string;
}

const initialState: State = {
  type: '',
  name: '',
  amount: '',
  baseCost: '',
  customerFeeID: '',
  quantity: 1,
  billableToCustomer: true,
  note: '',
};

export function OrderFeeForm({
  open,
  handleClose,
  orderId,
  orderFeeId,
  customerId,
  orderIds,
  isAppliedFee,
  shouldRefresh = true,
}: Props) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(initialState);
  const [specialOrderOptions, setSpecialOrderOptions] = useState([]);
  const [specialMaterialOptions, setSpecialMaterialOptions] = useState([]);
  const [specialHandlingOptions, setSpecialHandlingOptions] = useState([]);
  const [specialShipmentOptions, setSpecialShipmentOptions] = useState([]);
  const [customerFeesMapping, setCustomerFeesMapping] = useState({});

  useEffect(() => {
    (async function () {
      if (orderFeeId && orderId) {
        const { data } = await dispatch(
          fetchResourceById({
            baseUrl: '/orders',
            id: orderId,
            path: isAppliedFee
              ? `fees/${orderFeeId}`
              : `service-fees/${orderFeeId}`,
          }),
        );
        if (data) {
          const {
            customerFeeID,
            discountedRates,
            feeTypeDisplay,
            max,
            min,
            percentage,
            amount,
            baseCost,
            quantity,
            ...rest
          } = data;

          if (data.type) {
            // if a custom fee was generated somewhere else (ie refresh order fee), make sure it's in the list
            const specialFeeTypesOptions = customFeeTypes.map((t) => t.id);
            if (!specialFeeTypesOptions.includes(data.type)) {
              customFeeTypes.push({ id: data.type, name: feeTypeDisplay });
            }
          }

          setState((cur) => ({
            ...cur,
            ...convertNullFieldsToEmptyString(rest),
            amount: amount != null ? amount + (baseCost ?? 0) : '',
            quantity: quantity ?? 1,
          }));
        }
      } else if (customerId) {
        const { data } = await dispatch(
          fetchResourceById({
            baseUrl: '/customers',
            id: customerId,
            path: `fees/special-fees/basic`,
          }),
        );
        if (data) {
          let idAmountMapping = {};
          Object.keys(data).forEach((d) => {
            const fees = data[d];

            const mappings = fees.reduce((acc, cur) => {
              acc[cur.id.toString()] = cur.amount;
              return acc;
            }, {});

            idAmountMapping = { ...idAmountMapping, ...mappings };
            switch (d) {
              case 'SpecialOrderFee':
                setSpecialOrderOptions(fees);
                break;
              case 'SpecialMaterialFee':
                setSpecialMaterialOptions(fees);
                break;
              case 'SpecialHandlingFee':
                setSpecialHandlingOptions(fees);
                break;
              case 'SpecialShipmentFee':
                setSpecialShipmentOptions(fees);
                break;
              default:
                break;
            }
          });

          setCustomerFeesMapping(idAmountMapping);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orderFeeId, customerId, isAppliedFee]);

  const handleSubmit = useCallback(
    async (values) => {
      setSubmitting(true);
      const payload = {
        ...values,
        id: values.id || null,
      };
      if (!isAppliedFee) {
        payload.orderIDs = orderIds;
      }
      if (!orderIds) {
        const { error } = await dispatch(
          addResource({
            baseUrl: isAppliedFee
              ? `/orders/${orderId}/fees`
              : `/orders/${orderId}/service-fees`,
            payload,
            message: 'Changes saved',
          }),
        );
        setSubmitting(false);
        if (!error) {
          shouldRefresh && refresh();
          handleClose();
        }
      } else {
        const { error, data } = await dispatch(
          addResource({
            baseUrl: `/batches/add/order-service-fees`,
            payload,
          }),
        );
        setSubmitting(false);
        if (!error) {
          if (Array.isArray(data?.messages) && data.messages.length) {
            const msgDisplay = data.messages.map((m, i) => (
              <>
                <span key={i}>{m}</span>
                <br />
              </>
            ));
            notify(msgDisplay, 'error');
          } else {
            notify('Changes saved');
            shouldRefresh && refresh();
            handleClose();
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAppliedFee, orderIds, orderId, shouldRefresh],
  );

  const handleDelete = useCallback(async () => {
    if (!orderId) return;
    setSubmitting(true);
    const response = await dispatch(
      deleteResource({
        baseUrl: '/orders',
        id: orderId,
        path: isAppliedFee
          ? `fees/${orderFeeId}`
          : `service-fees/${orderFeeId}`,
      }),
    );
    setSubmitting(false);
    const { error } = response;
    if (!error) {
      refresh();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, isAppliedFee, orderFeeId]);

  const formik = useFormik({
    initialValues: { ...initialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  useEffect(() => {
    if (!orderFeeId) {
      formik.setFieldValue('customerFeeID', '');
      formik.setFieldValue('amount', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.type]);

  const handleTypeChange = useCallback(
    async (e) => {
      const { name, value } = e.target;
      formik.setFieldValue(name, value);
      const feeAmount = customerFeesMapping[value + ''] ?? 0;
      if (feeAmount) {
        formik.setFieldValue('amount', feeAmount);
      }
    },
    [customerFeesMapping, formik],
  );

  const { handleChange, ...formikProps } = formik;

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={
        <span className={classes.title}>{`${
          orderFeeId ? 'Edit' : 'Add'
        } special fee`}</span>
      }
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 468 } }}
      submitting={submitting}
      isEdit={!!orderFeeId}
      handleDelete={handleDelete}
    >
      <SelectInput
        name='type'
        formikProps={formik}
        label='Fee type'
        className={classes.input}
        options={customFeeTypes}
        disabled={!!orderFeeId}
      />
      {!!specialOrderOptions.length &&
        formik.values.type === 'SpecialOrderFee' && (
          <SelectInput
            name='customerFeeID'
            formikProps={formikProps}
            onChange={handleTypeChange}
            label='Select fee'
            className={classes.input}
            options={specialOrderOptions}
            clearable
          />
        )}
      {!!specialMaterialOptions.length &&
        formik.values.type === 'SpecialMaterialFee' && (
          <SelectInput
            name='customerFeeID'
            formikProps={formikProps}
            onChange={handleTypeChange}
            label='Select fee'
            className={classes.input}
            options={specialMaterialOptions}
            clearable
          />
        )}
      {!!specialHandlingOptions.length &&
        formik.values.type === 'SpecialHandlingFee' && (
          <SelectInput
            name='customerFeeID'
            formikProps={formikProps}
            onChange={handleTypeChange}
            label='Select fee'
            className={classes.input}
            options={specialHandlingOptions}
            clearable
          />
        )}
      {!!specialShipmentOptions.length &&
        formik.values.type === 'SpecialShipmentFee' && (
          <SelectInput
            name='customerFeeID'
            formikProps={formikProps}
            onChange={handleTypeChange}
            label='Select fee'
            className={classes.input}
            options={specialShipmentOptions}
            clearable
          />
        )}
      {!formik.values.customerFeeID && (
        <TextInput
          name='name'
          formikProps={formik}
          label='Fee name'
          className={classes.input}
        />
      )}
      {isAppliedFee && !orderFeeId && (
        <TextInput
          name='baseCost'
          formikProps={formik}
          label='Base cost'
          className={classes.input}
          format='currency'
        />
      )}
      <TextInput
        name='amount'
        formikProps={formik}
        label='Rate'
        className={classes.input}
        format='currency'
      />
      <TextInput
        name='quantity'
        formikProps={formik}
        label='Quantity'
        className={classes.input}
        type='number'
        slotProps={{ htmlInput: { min: 1 } }}
      />
      <TextInput
        name='note'
        label='Notes'
        formikProps={formik}
        className={classes.input}
        multiline
        rows={3}
      />
      {isAppliedFee && (
        <CheckboxInput
          formikProps={formik}
          checkboxes={[
            {
              labelProps: {
                label: 'Billable to customer',
              },
              checkboxProps: {
                name: 'billableToCustomer',
              },
            },
          ]}
        />
      )}
    </FormModal>
  );
}

const useStyles = makeStyles()((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  input: {
    marginBottom: 16,
    width: 300,
  },
}));

const schema = object().shape({
  quantity: string().required('Required'),
});

const customFeeTypes = [
  { id: 'SpecialOrderFee', name: 'Special Order Fee' },
  { id: 'SpecialHandlingFee', name: 'Special Handling Fee' },
  { id: 'SpecialMaterialFee', name: 'Special Material Fee' },
  { id: 'SpecialShipmentFee', name: 'Special Shipment Fee' },
];
